import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
    CalendarDays,
    ChevronLeft,
    ChevronRight,
    Search,
    Download,
    PlaneTakeoff,
    Clock,
    Receipt,
    HandCoins,
    Home as HomeIcon,
    MapPin,
    CalendarClock,
    Info,
    CheckCircle2,
    AlertCircle,
    X,
    Loader2,
} from "lucide-react";
import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
} from "../../components/ui/sheet";
import { Select } from "antd";
import { cn } from "../../utils/tailwindUtils";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { CALENDAR_EVENTS } from "../../constants/api";

const VIEW_MODES = ["Month", "Week", "Day"];
const WEEKDAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Where each quick / drawer action navigates. Shift-change is intentionally
// not wired — there's no backend endpoint for it yet.
const ROUTES = {
    leave: "/employee/leaverequest",
    wfh: "/employee/leaverequest",         // WFH lives inside the leave-request form
    on_duty: "/employee/leaverequest",     // same form, different leave_request_type
    reimbursement: "/employee/reimbursementrequest",
    advance: "/employee/advancerequest",
};

// Compute the [from, to] date range we need to load for a given view+cursor.
// Month view pads to start/end of week so the surrounding cells render too.
const computeRange = (cursor, view) => {
    if (view === "Month") {
        return {
            from: cursor.startOf("month").startOf("week").format("YYYY-MM-DD"),
            to: cursor.endOf("month").endOf("week").format("YYYY-MM-DD"),
        };
    }
    if (view === "Week") {
        return {
            from: cursor.startOf("week").format("YYYY-MM-DD"),
            to: cursor.endOf("week").format("YYYY-MM-DD"),
        };
    }
    return {
        from: cursor.format("YYYY-MM-DD"),
        to: cursor.format("YYYY-MM-DD"),
    };
};

const STATUS_PILL = {
    pending:   "bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-900/40 dark:text-orange-200 dark:border-orange-700",
    rejected:  "bg-red-100 text-red-700 border-red-300 dark:bg-red-900/40 dark:text-red-200 dark:border-red-700",
    cancelled: "bg-gray-200 text-gray-700 border-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600",
};

export const EVENT_TYPES = {
    leave_approved: { key: "leave_approved", label: "Leave (Approved)", color: "red", dot: "bg-red-500" },
    leave_pending: { key: "leave_pending", label: "Leave (Pending)", color: "orange", dot: "bg-orange-500" },
    holiday: { key: "holiday", label: "Holiday", color: "green", dot: "bg-green-500" },
    weekoff: { key: "weekoff", label: "Week Off", color: "gray", dot: "bg-gray-400" },
    shift: { key: "shift", label: "Shift", color: "blue", dot: "bg-blue-500" },
    attendance: { key: "attendance", label: "Attendance", color: "teal", dot: "bg-teal-500" },
    meeting: { key: "meeting", label: "Meeting", color: "purple", dot: "bg-purple-500" },
    reimbursement: { key: "reimbursement", label: "Reimbursement", color: "cyan", dot: "bg-cyan-500" },
    advance: { key: "advance", label: "Advance Salary", color: "amber", dot: "bg-amber-500" },
    wfh: { key: "wfh", label: "Work From Home", color: "indigo", dot: "bg-indigo-500" },
    on_duty: { key: "on_duty", label: "On Duty", color: "pink", dot: "bg-pink-500" },
    training_assigned: { key: "training_assigned", label: "Training Assigned", color: "fuchsia", dot: "bg-fuchsia-500" },
    training_due: { key: "training_due", label: "Training Due", color: "rose", dot: "bg-rose-500" },
};

export const eventTypeBadgeClass = (key) => {
    switch (key) {
        case "leave_approved": return "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800";
        case "leave_pending": return "bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800";
        case "holiday": return "bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800";
        case "weekoff": return "bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700";
        case "shift": return "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800";
        case "attendance": return "bg-teal-100 text-teal-700 border-teal-200 dark:bg-teal-900/30 dark:text-teal-300 dark:border-teal-800";
        case "meeting": return "bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800";
        case "reimbursement": return "bg-cyan-100 text-cyan-700 border-cyan-200 dark:bg-cyan-900/30 dark:text-cyan-300 dark:border-cyan-800";
        case "advance": return "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800";
        case "wfh": return "bg-indigo-100 text-indigo-700 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-800";
        case "on_duty": return "bg-pink-100 text-pink-700 border-pink-200 dark:bg-pink-900/30 dark:text-pink-300 dark:border-pink-800";
        case "training_assigned": return "bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200 dark:bg-fuchsia-900/30 dark:text-fuchsia-300 dark:border-fuchsia-800";
        case "training_due": return "bg-rose-100 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800";
        default: return "bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700";
    }
};

const needsStatusBadge = (ev) =>
    ev && ev.status && ev.status !== "approved" && STATUS_PILL[ev.status];

// Multi-day events are returned as one row with start_date/end_date.
// Fan them out into a per-day map so the calendar grid can render them in each cell.
const buildEventsByDate = (events) => {
    const map = {};
    for (const ev of events) {
        const start = dayjs(ev.start_date);
        const end = dayjs(ev.end_date || ev.start_date);
        let d = start;
        while (d.isBefore(end) || d.isSame(end, "day")) {
            const key = d.format("YYYY-MM-DD");
            (map[key] = map[key] || []).push(ev);
            d = d.add(1, "day");
        }
    }
    return map;
};

const Calendar = () => {
    const { fetch } = useApiMethods();
    const navigate = useNavigate();

    const goTo = (key, extra = {}) => {
        const path = ROUTES[key];
        if (!path) return;
        navigate(path, { state: { fromCalendar: true, ...extra } });
    };

    const [cursor, setCursor] = useState(dayjs());
    const [view, setView] = useState("Month");
    const [selectedDate, setSelectedDate] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);

    // Filters
    const [search, setSearch] = useState("");
    const [departmentId, setDepartmentId] = useState(null);
    const [employeeId, setEmployeeId] = useState(null);
    const [typeFilters, setTypeFilters] = useState([]);

    // API state
    const [events, setEvents] = useState([]);
    const [summary, setSummary] = useState({ leave_balance: null, pending_count: 0 });
    const [loading, setLoading] = useState(false);

    // ---------- Load events from API whenever range/scope changes ----------
    const { from, to } = useMemo(() => computeRange(cursor, view), [cursor, view]);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setLoading(true);
            try {
                const params = { from, to };
                if (departmentId) params.department_id = departmentId;
                if (employeeId) params.employee_id = employeeId;
                if (typeFilters.length) params.types = typeFilters.join(",");

                const response = await fetch(CALENDAR_EVENTS, params);
                if (cancelled) return;

                if (response?.status === "success") {
                    setEvents(response.data?.events || []);
                    setSummary(response.data?.summary || { leave_balance: null, pending_count: 0 });
                } else {
                    ToastMsg("error", response?.message || "Failed to load calendar.");
                    setEvents([]);
                }
            } catch (err) {
                if (!cancelled) {
                    ToastMsg("error", err?.message || "Failed to load calendar.");
                    setEvents([]);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => { cancelled = true; };
        // fetch from useApiMethods is a new reference every render — intentionally omitted.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [from, to, departmentId, employeeId, typeFilters]);

    // ---------- Client-side search filter ----------
    const filteredEvents = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return events;
        return events.filter((ev) => {
            const hay = `${ev.title} ${ev.description || ""}`.toLowerCase();
            return hay.includes(q);
        });
    }, [events, search]);

    const eventsByDate = useMemo(() => buildEventsByDate(filteredEvents), [filteredEvents]);

    const pendingRequests = useMemo(
        () => filteredEvents.filter((e) => e.status === "pending"),
        [filteredEvents]
    );

    // ---------- Navigation ----------
    const goPrev = () => {
        if (view === "Month") setCursor((c) => c.subtract(1, "month"));
        else if (view === "Week") setCursor((c) => c.subtract(1, "week"));
        else setCursor((c) => c.subtract(1, "day"));
    };
    const goNext = () => {
        if (view === "Month") setCursor((c) => c.add(1, "month"));
        else if (view === "Week") setCursor((c) => c.add(1, "week"));
        else setCursor((c) => c.add(1, "day"));
    };
    const goToday = () => setCursor(dayjs());

    const openDate = (date) => {
        setSelectedDate(date);
        setDrawerOpen(true);
    };

    // ---------- Header label ----------
    const headerLabel = useMemo(() => {
        if (view === "Month") return cursor.format("MMMM YYYY");
        if (view === "Week") {
            const start = cursor.startOf("week");
            const end = cursor.endOf("week");
            const sameMonth = start.month() === end.month();
            return sameMonth
                ? `${start.format("MMM D")} - ${end.format("D, YYYY")}`
                : `${start.format("MMM D")} - ${end.format("MMM D, YYYY")}`;
        }
        return cursor.format("dddd, MMM D, YYYY");
    }, [cursor, view]);

    // ---------- Export ----------
    const handleExport = () => {
        const header = "Start,End,Type,Title,Description\n";
        const rows = filteredEvents.map((e) =>
            [
                e.start_date,
                e.end_date,
                EVENT_TYPES[e.type]?.label || e.type,
                `"${e.title}"`,
                `"${e.description || ""}"`,
            ].join(",")
        );
        const blob = new Blob([header + rows.join("\n")], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `calendar-${cursor.format("YYYY-MM")}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <ContainerCard>
            <div className=" flex justify-between items-center">
                <ModuleTitleCard
                    Icon={CalendarDays}
                    title="Calendar"
                    subtitle="Centralized view of leaves, shifts, holidays, meetings & more"
                />
                <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 px-2 min-w-[180px]">
                    {headerLabel}
                    {loading && <Loader2 className="inline-block ml-2 w-4 h-4 animate-spin text-sidebar-primary" />}
                </h2>
            </div>

            {/* Toolbar */}
            <div className="px-3 mb-3">
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-3 flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-1">
                        <Button variant="outline" size="sm" onClick={goPrev} className="h-9 w-9 p-0">
                            <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={goToday} className="h-9 px-3">
                            Today
                        </Button>
                        <Button variant="outline" size="sm" onClick={goNext} className="h-9 w-9 p-0">
                            <ChevronRight className="w-4 h-4" />
                        </Button>
                    </div>

                    <div className="flex items-center bg-gray-100 dark:bg-gray-900 rounded-lg p-1">
                        {VIEW_MODES.map((m) => (
                            <button
                                key={m}
                                onClick={() => setView(m)}
                                className={cn(
                                    "px-3 py-1.5 text-xs font-medium rounded-md transition-all",
                                    view === m
                                        ? "bg-white dark:bg-gray-700 text-sidebar-primary shadow-sm"
                                        : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                                )}
                            >
                                {m}
                            </button>
                        ))}
                    </div>

                    <div className="ml-auto flex flex-wrap items-center gap-2">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search events..."
                                className="pl-9 pr-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-sidebar-primary/40 text-gray-800 dark:text-gray-200 w-48"
                            />
                        </div>

                        <Button variant="outline" size="sm" onClick={handleExport} className="h-9">
                            <Download className="w-4 h-4 mr-1" /> Export
                        </Button>
                    </div>
                </div>

                {/* Legend */}
                <div className="flex flex-wrap items-center gap-3 mt-3 px-1 text-xs">
                    {Object.values(EVENT_TYPES).map((t) => (
                        <div key={t.key} className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                            <span className={cn("w-2.5 h-2.5 rounded-full", t.dot)} />
                            {t.label}
                        </div>
                    ))}
                </div>
            </div>

            {/* Main grid */}
            <div className="grid grid-cols-1 xl:grid-cols-4 gap-3 px-3">
                <div className="xl:col-span-3">
                    {view === "Month" && (
                        <MonthView cursor={cursor} eventsByDate={eventsByDate} onDateClick={openDate} loading={loading} />
                    )}
                    {view === "Week" && (
                        <WeekView cursor={cursor} eventsByDate={eventsByDate} onDateClick={openDate} />
                    )}
                    {view === "Day" && (
                        <DayView cursor={cursor} eventsByDate={eventsByDate} />
                    )}
                </div>

                <aside className="xl:col-span-1 space-y-3">
                    <SidePanel
                        leaveBalance={summary.leave_balance}
                        pendingRequests={pendingRequests}
                        events={filteredEvents}
                        onNavigateDate={openDate}
                        goTo={goTo}
                    />
                </aside>
            </div>

            <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
                {drawerOpen && (
                    <SheetContent side="right" className="w-full sm:max-w-md overflow-auto">
                        <DateDrawer
                            date={selectedDate}
                            events={selectedDate ? eventsByDate[selectedDate.format("YYYY-MM-DD")] || [] : []}
                            leaveBalance={summary.leave_balance}
                            onClose={() => setDrawerOpen(false)}
                            goTo={goTo}
                        />
                    </SheetContent>
                )}
            </Sheet>
        </ContainerCard>
    );
};

/* =====================================================
   MONTH VIEW
===================================================== */
const MonthView = ({ cursor, eventsByDate, onDateClick, loading }) => {
    const gridStart = cursor.startOf("month").startOf("week");
    const gridEnd = cursor.endOf("month").endOf("week");
    const today = dayjs();

    const days = [];
    let d = gridStart;
    while (d.isBefore(gridEnd) || d.isSame(gridEnd, "day")) {
        days.push(d);
        d = d.add(1, "day");
    }

    return (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden relative">
            {loading && (
                <div className="absolute inset-0 bg-white/40 dark:bg-gray-900/40 z-10 flex items-center justify-center pointer-events-none">
                    <Loader2 className="w-6 h-6 animate-spin text-sidebar-primary" />
                </div>
            )}
            <div className="grid grid-cols-7 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                {WEEKDAYS_SHORT.map((w) => (
                    <div key={w} className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 text-center">
                        {w}
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-7 auto-rows-fr">
                {days.map((day) => {
                    const dateKey = day.format("YYYY-MM-DD");
                    const events = eventsByDate[dateKey] || [];
                    const inMonth = day.month() === cursor.month();
                    const isToday = day.isSame(today, "day");
                    const isWeekend = day.day() === 0 || day.day() === 6;

                    return (
                        <button
                            key={dateKey}
                            onClick={() => onDateClick(day)}
                            className={cn(
                                "min-h-[110px] text-left p-2 border-r border-b border-gray-100 dark:border-gray-700/50 transition-all hover:bg-gray-50 dark:hover:bg-gray-900/50 focus:outline-none focus:ring-2 focus:ring-sidebar-primary/40 focus:ring-inset",
                                !inMonth && "bg-gray-50/50 dark:bg-gray-900/30",
                                isWeekend && inMonth && "bg-gray-50/40 dark:bg-gray-900/20"
                            )}
                        >
                            <div className="flex items-center justify-between mb-1">
                                <span
                                    className={cn(
                                        "inline-flex items-center justify-center text-xs font-medium w-6 h-6 rounded-full",
                                        isToday
                                            ? "bg-sidebar-primary text-white"
                                            : inMonth
                                                ? "text-gray-800 dark:text-gray-200"
                                                : "text-gray-400 dark:text-gray-600"
                                    )}
                                >
                                    {day.date()}
                                </span>
                                {events.length > 0 && (
                                    <span className="text-[10px] text-gray-400 dark:text-gray-500 font-medium">
                                        {events.length}
                                    </span>
                                )}
                            </div>
                            <div className="space-y-1">
                                {events.slice(0, 3).map((ev) => (
                                    <div
                                        key={ev.id}
                                        title={`${ev.title}${needsStatusBadge(ev) ? " (" + ev.status + ")" : ""}${ev.description ? " - " + ev.description : ""}`}
                                        className={cn(
                                            "text-[10px] px-1.5 py-0.5 rounded border truncate flex items-center gap-1",
                                            eventTypeBadgeClass(ev.type),
                                            needsStatusBadge(ev) && "border-dashed ring-1 ring-orange-300/70 dark:ring-orange-600/60"
                                        )}
                                    >
                                        {needsStatusBadge(ev) && (
                                            <span className="text-[8px] leading-none">⏳</span>
                                        )}
                                        <span className="truncate">{ev.title}</span>
                                    </div>
                                ))}
                                {events.length > 3 && (
                                    <div className="text-[10px] text-gray-500 dark:text-gray-400 px-1">
                                        +{events.length - 3} more
                                    </div>
                                )}
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

/* =====================================================
   WEEK VIEW
===================================================== */
const WeekView = ({ cursor, eventsByDate, onDateClick }) => {
    const start = cursor.startOf("week");
    const today = dayjs();
    const days = Array.from({ length: 7 }, (_, i) => start.add(i, "day"));

    return (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
            <div className="grid grid-cols-7 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                {days.map((day) => {
                    const isToday = day.isSame(today, "day");
                    return (
                        <div key={day.format("YYYY-MM-DD")} className="p-3 text-center border-r border-gray-100 dark:border-gray-700/50 last:border-r-0">
                            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{day.format("ddd")}</div>
                            <div className={cn(
                                "mt-1 inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold",
                                isToday ? "bg-sidebar-primary text-white" : "text-gray-800 dark:text-gray-100"
                            )}>
                                {day.date()}
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="grid grid-cols-7 min-h-[500px]">
                {days.map((day) => {
                    const events = eventsByDate[day.format("YYYY-MM-DD")] || [];
                    return (
                        <button
                            key={day.format("YYYY-MM-DD") + "col"}
                            onClick={() => onDateClick(day)}
                            className="text-left p-2 border-r border-gray-100 dark:border-gray-700/50 last:border-r-0 hover:bg-gray-50 dark:hover:bg-gray-900/50 transition-colors space-y-1.5"
                        >
                            {events.length === 0 && (
                                <div className="text-[11px] text-gray-300 dark:text-gray-600 italic pt-2">No events</div>
                            )}
                            {events.map((ev) => (
                                <div
                                    key={ev.id}
                                    className={cn(
                                        "text-xs px-2 py-1.5 rounded-md border",
                                        eventTypeBadgeClass(ev.type),
                                        needsStatusBadge(ev) && "border-dashed ring-1 ring-orange-300/70 dark:ring-orange-600/60"
                                    )}
                                >
                                    <div className="font-medium truncate flex items-center gap-1">
                                        {needsStatusBadge(ev) && <span className="text-[10px]">⏳</span>}
                                        <span className="truncate">{ev.title}</span>
                                        {needsStatusBadge(ev) && (
                                            <span className={cn(
                                                "ml-auto text-[8px] uppercase font-bold tracking-wide px-1 py-0.5 rounded-sm border",
                                                STATUS_PILL[ev.status]
                                            )}>
                                                {ev.status}
                                            </span>
                                        )}
                                    </div>
                                    {ev.description && (
                                        <div className="opacity-75 truncate mt-0.5">{ev.description}</div>
                                    )}
                                </div>
                            ))}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

/* =====================================================
   DAY VIEW
===================================================== */
const DayView = ({ cursor, eventsByDate }) => {
    const events = eventsByDate[cursor.format("YYYY-MM-DD")] || [];

    return (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5">
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200 dark:border-gray-700">
                <div>
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                        {cursor.format("dddd")}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{cursor.format("MMMM D, YYYY")}</p>
                </div>
                <div className="text-right">
                    <div className="text-2xl font-bold text-sidebar-primary">{events.length}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 uppercase">Events</div>
                </div>
            </div>

            {events.length === 0 ? (
                <div className="text-center py-12 text-gray-400 dark:text-gray-500">
                    <CalendarDays className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">No events scheduled for this day</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {events.map((ev) => {
                        const t = EVENT_TYPES[ev.type];
                        return (
                            <div
                                key={ev.id}
                                className={cn(
                                    "flex items-start gap-3 p-3 rounded-lg border",
                                    eventTypeBadgeClass(ev.type),
                                    needsStatusBadge(ev) && "ring-1 ring-orange-300 dark:ring-orange-600/60"
                                )}
                            >
                                <span className={cn("w-3 h-3 rounded-full mt-1.5 shrink-0", t?.dot)} />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between gap-2">
                                        <h4 className="font-semibold text-sm flex items-center gap-2">
                                            {ev.title}
                                            {needsStatusBadge(ev) && (
                                                <span className={cn(
                                                    "text-[9px] uppercase font-bold tracking-wide px-1.5 py-0.5 rounded-full border",
                                                    STATUS_PILL[ev.status]
                                                )}>
                                                    {ev.status === "pending" ? "⏳ Pending" : ev.status}
                                                </span>
                                            )}
                                        </h4>
                                        <span className="text-[11px] uppercase tracking-wide opacity-75 shrink-0">{t?.label}</span>
                                    </div>
                                    {ev.description && (
                                        <p className="text-xs mt-1 opacity-80">{ev.description}</p>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

/* =====================================================
   SIDE PANEL
===================================================== */
const SidePanel = ({ leaveBalance, pendingRequests, events, onNavigateDate, goTo }) => {
    // leaveBalance shape is loose (depends on home.utils.calculate_leaves output);
    // pluck a few sensible fields if present, else show a friendly empty state.
    const balanceEntries = useMemo(() => {
        if (!leaveBalance || typeof leaveBalance !== "object") return [];
        const candidate =
            leaveBalance.balance ||
            leaveBalance.available_leaves ||
            leaveBalance;
        if (typeof candidate === "number") {
            return [{ label: "Available", value: candidate }];
        }
        return Object.entries(candidate)
            .filter(([, v]) => typeof v === "number" || typeof v === "string")
            .slice(0, 6)
            .map(([k, v]) => ({ label: k, value: v }));
    }, [leaveBalance]);

    // Stats: counts per event type in the currently visible range
    const stats = useMemo(() => {
        const counts = {
            leave_approved: 0,
            leave_pending: 0,
            wfh: 0,
            on_duty: 0,
            holiday: 0,
            meeting: 0,
            reimbursement: 0,
            advance: 0,
        };
        for (const e of events) {
            if (counts[e.type] !== undefined) counts[e.type] += 1;
        }
        return counts;
    }, [events]);

    // Upcoming holidays (today onwards) — top 4
    const upcomingHolidays = useMemo(() => {
        const today = dayjs().startOf("day");
        return events
            .filter((e) => {
                if (e.type !== "holiday") return false;
                const d = dayjs(e.start_date);
                return d.isSame(today, "day") || d.isAfter(today, "day");
            })
            .sort((a, b) => dayjs(a.start_date).valueOf() - dayjs(b.start_date).valueOf())
            .slice(0, 4);
    }, [events]);

    // Today's plan — events covering today
    const todaysPlan = useMemo(() => {
        const today = dayjs();
        return events.filter((e) => {
            const s = dayjs(e.start_date);
            const en = dayjs(e.end_date || e.start_date);
            return (today.isSame(s, "day") || today.isAfter(s, "day")) &&
                (today.isSame(en, "day") || today.isBefore(en, "day"));
        });
    }, [events]);

    return (
        <>
            {/* Leave balance */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-4">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300 flex items-center gap-2 mb-3">
                    <PlaneTakeoff className="w-4 h-4 text-sidebar-primary" /> Leave Balance
                </h3>
                {balanceEntries.length === 0 ? (
                    <p className="text-xs text-gray-400 text-center py-4">No balance data</p>
                ) : (
                    <div className="grid grid-cols-3 gap-2">
                        {balanceEntries.map((b, i) => (
                            <BalanceCell key={b.label + i} label={b.label} value={b.value} />
                        ))}
                    </div>
                )}
            </div>

            {/* Month snapshot */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-4">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300 flex items-center gap-2 mb-3">
                    <CalendarDays className="w-4 h-4 text-sidebar-primary" /> This Month
                </h3>
                <div className="grid grid-cols-2 gap-2">
                    <StatTile color="red" label="Leaves" value={stats.leave_approved} />
                    <StatTile color="orange" label="Pending" value={stats.leave_pending} />
                    <StatTile color="green" label="Holidays" value={stats.holiday} />
                    <StatTile color="indigo" label="WFH" value={stats.wfh} />
                    <StatTile color="purple" label="Meetings" value={stats.meeting} />
                    <StatTile color="cyan" label="Claims" value={stats.reimbursement + stats.advance} />
                </div>
            </div>

            {/* Today's plan */}
            {todaysPlan.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-4">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300 flex items-center gap-2 mb-3">
                        <Clock className="w-4 h-4 text-teal-500" /> Today's Plan
                        <span className="ml-auto text-xs bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 px-2 py-0.5 rounded-full">
                            {todaysPlan.length}
                        </span>
                    </h3>
                    <ul className="space-y-2 max-h-48 overflow-auto">
                        {todaysPlan.map((e) => (
                            <li
                                key={e.id}
                                onClick={() => onNavigateDate(dayjs(e.start_date))}
                                className="flex items-start gap-2 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-900/50 cursor-pointer transition-colors border border-transparent hover:border-gray-200 dark:hover:border-gray-700"
                            >
                                <span className={cn("w-2 h-2 mt-1.5 rounded-full shrink-0", EVENT_TYPES[e.type]?.dot)} />
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium text-gray-800 dark:text-gray-100 truncate">{e.title}</p>
                                    <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                                        {EVENT_TYPES[e.type]?.label}
                                        {e.description ? ` • ${e.description}` : ""}
                                    </p>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Upcoming holidays */}
            {upcomingHolidays.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-4">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300 flex items-center gap-2 mb-3">
                        <Info className="w-4 h-4 text-green-500" /> Upcoming Holidays
                    </h3>
                    <ul className="space-y-2">
                        {upcomingHolidays.map((h) => (
                            <li
                                key={h.id}
                                onClick={() => onNavigateDate(dayjs(h.start_date))}
                                className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-900/50 cursor-pointer transition-colors border border-transparent hover:border-gray-200 dark:hover:border-gray-700"
                            >
                                <div className="flex flex-col items-center justify-center w-10 h-10 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 shrink-0">
                                    <span className="text-[10px] font-medium uppercase leading-none">{dayjs(h.start_date).format("MMM")}</span>
                                    <span className="text-sm font-bold leading-none">{dayjs(h.start_date).format("D")}</span>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium text-gray-800 dark:text-gray-100 truncate">{h.title}</p>
                                    <p className="text-[11px] text-gray-500 dark:text-gray-400">
                                        {dayjs(h.start_date).format("dddd")}
                                        {h.description ? ` • ${h.description}` : ""}
                                    </p>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Pending requests */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-4">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300 flex items-center gap-2 mb-3">
                    <AlertCircle className="w-4 h-4 text-orange-500" /> Pending Requests
                    {pendingRequests.length > 0 && (
                        <span className="ml-auto text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 px-2 py-0.5 rounded-full">
                            {pendingRequests.length}
                        </span>
                    )}
                </h3>
                {pendingRequests.length === 0 ? (
                    <p className="text-xs text-gray-400 text-center py-4">No pending requests</p>
                ) : (
                    <ul className="space-y-2 max-h-64 overflow-auto">
                        {pendingRequests.map((r) => (
                            <li
                                key={r.id}
                                onClick={() => onNavigateDate(dayjs(r.start_date))}
                                className="flex items-start gap-2 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-900/50 cursor-pointer transition-colors border border-transparent hover:border-gray-200 dark:hover:border-gray-700"
                            >
                                <span className={cn("w-2 h-2 mt-1.5 rounded-full shrink-0", EVENT_TYPES[r.type]?.dot)} />
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium text-gray-800 dark:text-gray-100 truncate">{r.title}</p>
                                    <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                                        {dayjs(r.start_date).format("MMM D")}
                                        {r.employee_name ? ` • ${r.employee_name}` : ""}
                                    </p>
                                </div>
                                <span className="text-[10px] uppercase font-semibold text-orange-600 dark:text-orange-400 mt-1 shrink-0">
                                    {EVENT_TYPES[r.type]?.label?.split(" ")[0]}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Quick actions */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-4">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300 mb-3">
                    Quick Actions
                </h3>
                <div className="grid grid-cols-2 gap-2">
                    <QuickAction icon={PlaneTakeoff} label="Apply Leave" onClick={() => goTo("leave")} />
                    <QuickAction icon={Receipt} label="Reimbursement" onClick={() => goTo("reimbursement")} />
                    <QuickAction icon={HandCoins} label="Advance" onClick={() => goTo("advance")} />
                    <QuickAction icon={CalendarClock} label="Shift Change" disabled />
                </div>
            </div>
        </>
    );
};

const StatTile = ({ color, label, value }) => {
    const tones = {
        red: "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300",
        orange: "bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300",
        green: "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300",
        indigo: "bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300",
        purple: "bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300",
        cyan: "bg-cyan-50 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-300",
    };
    return (
        <div className={cn("rounded-lg p-2 flex items-center justify-between", tones[color])}>
            <span className="text-[10px] uppercase tracking-wide font-semibold truncate">{label}</span>
            <span className="text-base font-bold leading-none">{value}</span>
        </div>
    );
};

const BalanceCell = ({ label, value }) => (
    <div className="rounded-lg p-2 text-center bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300">
        <div className="text-lg font-bold">{value}</div>
        <div className="text-[10px] uppercase tracking-wide font-medium truncate" title={label}>{label}</div>
    </div>
);

const QuickAction = ({ icon: Icon, label, onClick, disabled }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        title={disabled ? "Coming soon" : label}
        className={cn(
            "flex items-center gap-2 p-2 rounded-lg border border-gray-200 dark:border-gray-700 transition-colors text-xs text-gray-700 dark:text-gray-300",
            disabled
                ? "opacity-50 cursor-not-allowed"
                : "hover:border-sidebar-primary hover:bg-sidebar-primary/5"
        )}
    >
        <Icon className="w-3.5 h-3.5 text-sidebar-primary" />
        <span className="truncate">{label}</span>
    </button>
);

/* =====================================================
   DATE DRAWER
===================================================== */
const DateDrawer = ({ date, events, leaveBalance, onClose, goTo }) => {
    if (!date) return null;
    const isToday = date.isSame(dayjs(), "day");
    const isHoliday = events.some((e) => e.type === "holiday");
    const isWeekOff = events.some((e) => e.type === "weekoff");
    const onLeave = events.some((e) => e.type === "leave_approved");
    const attendance = events.find((e) => e.type === "attendance");
    const shift = events.find((e) => e.type === "shift");

    return (
        <div className="space-y-4 pr-2">
            <SheetHeader>
                <div className="flex items-center gap-3">
                    <div className="bg-sidebar-primary/10 text-sidebar-primary rounded-lg p-2.5">
                        <CalendarDays className="w-5 h-5" />
                    </div>
                    <div>
                        <SheetTitle className="text-base">{date.format("dddd, MMM D")}</SheetTitle>
                        <SheetDescription>
                            {date.format("YYYY")} {isToday && <span className="ml-1 px-1.5 py-0.5 bg-sidebar-primary/10 text-sidebar-primary text-[10px] rounded-full font-medium">TODAY</span>}
                        </SheetDescription>
                    </div>
                </div>
            </SheetHeader>

            <div className="space-y-2">
                {isHoliday && (
                    <GuidanceLine icon={Info} tone="green" text="This is a public holiday." />
                )}
                {isWeekOff && (
                    <GuidanceLine icon={Info} tone="gray" text="This is a weekly off day." />
                )}
                {onLeave && (
                    <GuidanceLine icon={Info} tone="red" text="You already have a leave approved on this date." />
                )}
                {attendance && (
                    <GuidanceLine icon={CheckCircle2} tone="teal" text={`Attendance marked: ${attendance.description || attendance.title}`} />
                )}
                {shift && (
                    <GuidanceLine icon={Clock} tone="blue" text={`Shift assigned: ${shift.title}${shift.description ? " (" + shift.description + ")" : ""}`} />
                )}
                {!isHoliday && !isWeekOff && !onLeave && leaveBalance && (
                    <GuidanceLine icon={CheckCircle2} tone="teal" text="Leave balance is available — see side panel." />
                )}
            </div>

            <div>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                    Events on this day ({events.length})
                </h4>
                {events.length === 0 ? (
                    <p className="text-xs text-gray-400 italic py-3 text-center border border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
                        No events scheduled.
                    </p>
                ) : (
                    <div className="space-y-2">
                        {events.map((ev) => {
                            const t = EVENT_TYPES[ev.type];
                            return (
                                <div
                                    key={ev.id}
                                    className={cn(
                                        "rounded-lg border p-2.5 relative",
                                        eventTypeBadgeClass(ev.type),
                                        needsStatusBadge(ev) && "ring-1 ring-orange-300 dark:ring-orange-600/60"
                                    )}
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="text-xs font-medium flex items-center gap-2">
                                            {ev.title}
                                            {needsStatusBadge(ev) && (
                                                <span className={cn(
                                                    "text-[9px] uppercase font-bold tracking-wide px-1.5 py-0.5 rounded-full border",
                                                    STATUS_PILL[ev.status]
                                                )}>
                                                    {ev.status === "pending" ? "⏳ Pending" : ev.status}
                                                </span>
                                            )}
                                        </span>
                                        <span className="text-[10px] uppercase font-semibold opacity-75 shrink-0">{t?.label}</span>
                                    </div>
                                    {ev.description && (
                                        <p className="text-[11px] mt-1 opacity-80">{ev.description}</p>
                                    )}
                                    {ev.meta?.requested_amount != null && (
                                        <p className="text-[11px] mt-1 font-semibold">
                                            ₹ {ev.meta.requested_amount}
                                            {ev.meta.approved_amount != null && ev.meta.approved_amount !== ev.meta.requested_amount && (
                                                <span className="opacity-75 font-normal"> &nbsp;(approved: ₹ {ev.meta.approved_amount})</span>
                                            )}
                                            {ev.meta.is_paid && <span className="ml-2 text-[10px] uppercase opacity-75">• paid</span>}
                                        </p>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <div>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                    Actions
                </h4>
                <div className="grid grid-cols-2 gap-2">
                    <ActionButton
                        icon={PlaneTakeoff}
                        label="Apply Leave"
                        disabled={isHoliday || onLeave}
                        onClick={() => goTo("leave", { prefillDate: date.format("YYYY-MM-DD") })}
                    />
                    <ActionButton
                        icon={HomeIcon}
                        label="Apply WFH"
                        onClick={() => goTo("wfh", { prefillDate: date.format("YYYY-MM-DD"), leave_request_type: "work_from_home" })}
                    />
                    <ActionButton
                        icon={MapPin}
                        label="Apply On Duty"
                        onClick={() => goTo("on_duty", { prefillDate: date.format("YYYY-MM-DD"), leave_request_type: "on_duty" })}
                    />
                    <ActionButton icon={CalendarClock} label="Request Shift Change" disabled />
                    <ActionButton
                        icon={Receipt}
                        label="Reimbursement"
                        onClick={() => goTo("reimbursement", { prefillDate: date.format("YYYY-MM-DD") })}
                    />
                    <ActionButton
                        icon={HandCoins}
                        label="Advance Salary"
                        onClick={() => goTo("advance")}
                    />
                </div>
            </div>

            <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                <Button variant="outline" size="sm" onClick={onClose} className="w-full">
                    <X className="w-4 h-4 mr-1" /> Close
                </Button>
            </div>
        </div>
    );
};

const GuidanceLine = ({ icon: Icon, tone, text }) => {
    const tones = {
        green: "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800",
        red: "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800",
        blue: "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800",
        teal: "bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300 border-teal-200 dark:border-teal-800",
        gray: "bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700",
    };
    return (
        <div className={cn("flex items-start gap-2 p-2.5 rounded-lg border text-xs", tones[tone])}>
            <Icon className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{text}</span>
        </div>
    );
};

const ActionButton = ({ icon: Icon, label, disabled, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={cn(
            "flex items-center gap-1.5 p-2 rounded-lg border border-gray-200 dark:border-gray-700 text-xs text-gray-700 dark:text-gray-300 transition-colors",
            disabled
                ? "opacity-50 cursor-not-allowed"
                : "hover:border-sidebar-primary hover:bg-sidebar-primary/5"
        )}
    >
        <Icon className="w-3.5 h-3.5 text-sidebar-primary" />
        <span className="truncate text-left">{label}</span>
    </button>
);

export default Calendar;
